import React, { useState } from "react";
import { Button, Input, PopupModal, SVG } from "../../shared";
import Select from "../../shared/Select/Select";
import { Field, Formik, Form } from "formik";
import { SVG_ENUM, VALIDATION_ENUM } from "../../enums";
import * as Yup from "yup";
import { useCreateAccount, useUpdateUser } from "../../hooks/users";
import { UserType } from "../../types/UsersTypes";
import { SelectOption } from "../../types/SelectTypes";
import "./AddEditUserModal.scss";

interface AddEditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  user?: UserType | null; // If user is provided, we're editing; if null/undefined, we're adding
}

const AddEditUserModal: React.FC<AddEditUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  user = null,
}) => {
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const roleOptions: SelectOption[] = [
    { value: "user", label: "Użytkownik" },
    { value: "admin", label: "Administrator" },
  ];

  const isEditMode = !!user;

  const { mutate: createUser } = useCreateAccount({
    onSuccess: () => {
      onClose();
      setShowError(false);
      setErrorMessage("");
      onSuccess?.();
    },
    onError: (error: Error) => {
      setShowError(true);
      setErrorMessage("Błąd podczas tworzenia konta");
    },
  });

  const { mutate: updateUser } = useUpdateUser({
    onSuccess: () => {
      onClose();
      setShowError(false);
      setErrorMessage("");
      onSuccess?.();
    },
    onError: (error: Error) => {
      setShowError(true);
      setErrorMessage("Błąd podczas aktualizacji konta");
    },
  });

  const validation = () =>
    Yup.object().shape({
      name: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .min(2, "Imię i nazwisko musi mieć co najmniej 2 znaki"),
      email: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .email("Nieprawidłowy format email"),
      role: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .oneOf(["admin", "user"], "Nieprawidłowa rola"),
    });

  const initialValues = {
    name: user?.name || "",
    email: user?.email || "",
    role: user?.role || ("user" as "admin" | "user"),
  };

  const onSubmit = (values: typeof initialValues) => {
    if (isEditMode && user) {
      updateUser({
        id: user.id,
        name: values.name,
        email: values.email,
        role: values.role,
      });
    } else {
      createUser({
        name: values.name,
        email: values.email,
        role: values.role,
      });
    }
  };

  const handleClose = () => {
    setShowError(false);
    setErrorMessage("");
    onClose();
  };

  return (
    <PopupModal isOpen={isOpen} onClose={handleClose}>
      <div className="add-edit-user-modal">
        <div className="add-edit-user-modal__header">
          <h2 className="popup-modal__header">
            {isEditMode ? "Edytuj użytkownika" : "Dodaj użytkownika"}
          </h2>
        </div>

        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validation()}
          enableReinitialize={true}
        >
          {({ errors, values, handleSubmit, setFieldValue }) => (
            <Form>
              {showError && (
                <p className="add-edit-user-modal__error">
                  {errorMessage}
                  <SVG type={SVG_ENUM.CLOSE_CIRCLE} />
                </p>
              )}

              <Field
                type="text"
                id="name"
                name="name"
                label="Imię i nazwisko"
                placeholder="Wprowadź imię i nazwisko"
                onFocus={() => setShowError(false)}
                as={Input}
              />

              <Field
                type="email"
                id="email"
                name="email"
                label="Email"
                placeholder="Wprowadź adres email"
                onFocus={() => setShowError(false)}
                as={Input}
              />

              <Field
                name="role"
                label="Rola"
                options={roleOptions}
                placeholder="Wybierz rolę"
                onFocus={() => setShowError(false)}
                as={Select}
              />

              <div className="popup-modal__buttons">
                <Button
                  label="Anuluj"
                  styleType="stroke"
                  onClick={handleClose}
                  type="button"
                />
                <Button
                  label={isEditMode ? "Zaktualizuj" : "Dodaj użytkownika"}
                  className="button--primary"
                  styleType="primary"
                  type="submit"
                  onClick={() => {}}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </PopupModal>
  );
};

export default AddEditUserModal;
